import type { ReactElement } from 'react'
import styled from 'styled-components'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { Layout } from '@/components/layouts/Ir'
import { BulletedList } from '@/components/layouts/Ir/BulletedList'
import { AHeading, BHeading } from '@/components/layouts/Ir/Heading'
import { Paragraph } from '@/components/layouts/Ir/Paragraph'

export const DisclosurePage = () => {
  return (
    <Layout>
      <SectionTitle
        color="normal"
        titleText="Disclosure / IR Policy"
        subTitleText="ディスクロージャー・IRポリシー"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <section>
        <AHeading>ディスクロージャー・ポリシー</AHeading>
        <Paragraph>
          私たちトライアルグループは、流通×IT の組み合わせによって、リテール DX を推進して、流通革命を起こすことを目指しています。
        </Paragraph>
        <Paragraph>
          『世界の誰もが「豊かさ」を享受できる社会をつくる。』をパーパスに掲げて、私たちはステークホルダーの皆さまに対して、迅速、正確かつ公平なディスクロージャーを実行しながら、建設的な対話に努めます。 
        </Paragraph>
        <Paragraph>
          その継続により、経営の透明性を高めると同時に、企業の社会的責任を果たしながら、信頼いただけるように努めます。
        </Paragraph>
        <Paragraph>
          この基本方針を社内外に周知するとともに、自らのディスクロージャーを常に、適正な基準、方法、体制で実行するために、ディスクロージャー・ポリシーを以下の通り定めます。
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 80 }} />
        <BHeading>1. 情報開示の基準</BHeading>
        <Paragraph>
          当社は、会社法、金融商品取引法その他諸法令ならびに東京証券取引所の「有価証券上場規程」に定める会社情報の適時開示に関する規定（以下、「適時開示規則」）に基づく一貫した情報開示を行います。
        </Paragraph>
        <Paragraph>
          投資判断に重要な影響を与える決定事実、発生事実、決算に関する情報が生じた場合には、適時開示規則の基準に沿って迅速に開示するとともに、適時開示規則に該当しない場合でも、ステークホルダーの方々に当社を理解いただくために重要あるいは有益であると判断した情報につきましては積極的に開示し、情報アクセスの公平性向上に努めます。
        </Paragraph>
        <Paragraph>
          なお、個人情報、顧客情報、及び関係者の権利を侵害することになる情報につきましては開示しません。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>2. 情報開示の方法</BHeading>
        <Paragraph>
          適時開示規則に該当する情報につきましては、東京証券取引所が提供する「TDnet（適時開示情報伝達システム）」へ登録し公開した後、速やかに当社Webサイトに掲載します。
        </Paragraph>
        <Paragraph>
          適時開示規則に該当しない情報につきましても、Webサイトへの掲載等により広く開示します。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>3. インサイダー取引の未然防止</BHeading>
        <Paragraph>
          当社は、重要情報を適切に管理しインサイダー取引の未然防止を図るための社内規程を定め、グループ社員全体への周知徹底と理解啓蒙を促進しています。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>4. 業績予想及び将来情報の取り扱い</BHeading>
        <Paragraph>
          当社が開示する業績予想、戦略、方針、目標等のうち、歴史的事実ではないものは将来の見通しに関する記述であり、これらは、当社がその時点で入手している情報及び合理的であると判断される一定の前提に基づく計画、期待、判断を根拠としており、実際の業績等は様々なリスクや不確定要因により大きく異なる可能性があります。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>5. 沈黙期間</BHeading>
        <Paragraph>
          当社は、ステークホルダーの方々への公平性を確保するため、各四半期の期末日の翌日から各決算発表日までを沈黙期間としています。
        </Paragraph>
        <Paragraph>
          この期間中は、決算・業績見通しに関する質問への回答やコメントを差し控えております。ただし、沈黙期間中に業績予想を大きく外れる見込が出てきたときには、適時開示規則に従い、適宜情報開示を行います。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>6. 社内体制の整備について</BHeading>
        <Paragraph>
          当社はディスクロージャー・ポリシーを適切に運用し、適時開示規則に基づく適時、適切な情報開示を実行する体制を構築し維持します。
        </Paragraph>
        <Paragraph>
          当社は、情報開示担当部門が社内の各業務執行部門との連繋により適切な情報収集と分析を行い、会社として適切な判断の上で開示を行うために、情報の発生から開示に至るプロセスを明確化しています。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 30 }} />
        <Image
          src="/images/ir-timely-disclosure-system_JP.png"
          alt="適時開示体制"
          width="100%"
        />
        <Spacer h={{ sp: 80, pc: 100 }} />
        <AHeading>IRポリシー</AHeading>
        <Paragraph>
          当社は、流通×ITの組み合わせによって、流通小売業界のムダ・ムラ・ムリの解消に貢献しながら、お客様の役に立つという使命を担っています。当社のIR活動は、株主や投資家など、ステークホルダーの皆さまに対して、迅速、正確かつ公平なディスクロージャーと建設的な対話を実行すべく、下記方針のもとにIR活動を推進します。
        </Paragraph>
        <Paragraph>
          当社の企業価値を高めるためには、ステークホルダーの方々に当社を正しくご理解いただくことが不可欠です。そのために当社は、経営理念、使命・役割、経営方針、事業内容、業績、財務内容、各種の経営指標、株主還元の方針と実績、中期経営計画と今後の展望等について、ディスクロージャー・ポリシーに則り、正しく、タイムリーに開示いたします。
        </Paragraph>
        <Spacer h={{ sp: 20, pc: 30 }} />
        <Paragraph>
          株主・投資家などステークホルダーの皆さまとの対話は、IR担当部門及び各部門の経営陣が担います。対話の実効性を高めるため、IR担当部門が中心となり、財務・経理部門、総務部門等のコーポレート部門と密接な情報連携を図り、建設的な対話の前提として、以下のような取組みを行います。
        </Paragraph>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <BulletedList>
          <li>決算説明会、会社説明会、施設見学会の実施</li>
          <li>機関投資家向け、個別取材の応対</li>
          <li>決算・会社説明会資料等をウェブサイトで公表</li>
        </BulletedList>
        <Spacer h={{ sp: 30, pc: 50 }} />
        <Paragraph>
          コミュニケーションを通じてステークホルダーの皆さまから得られた知見等は、経営判断に役立てるよう経営陣に随時報告します。
        </Paragraph>
        <Paragraph>
          IR活動を通して、ステークホルダーの皆さまの当社に対するご理解を適切に深めていただくとともに、当社の企業価値、ブランド価値の向上を目指します。
        </Paragraph>
      </section>
    </Layout>
  )
}

DisclosurePage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '株主・投資家向け情報', path: '/ir' },
    { name: '経営方針', path: '/ir/manage' },
    { name: 'ディスクロージャー・IRポリシー', path: '' },
  ]

  return (
    <>
      <Head
        title="ディスクロージャー・IRポリシー"
        description="トライアルホールディングスのIR活動における基本方針について掲載しております。"
        ogp={{ url: 'ir/manage/disclosure/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}

const Image = styled.img`
  height: 100%;
`
